import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import { PageLoader } from "@/components/Kit";
import { playSfx, playJingle } from "@/lib/sound";
import { Swords, Zap, Shield, Trophy, Skull, ArrowLeft, Flame, Users, Crosshair, User, Layers } from "lucide-react";

function Bar({ label, hp, max, color, testId }) {
  const pct = Math.max(0, Math.min(100, (hp / max) * 100));
  return (<div data-testid={testId}><div className="flex justify-between label-cap mb-1"><span>{label}</span><span>{Math.max(0, hp)}/{max}</span></div>
    <div className="h-3 rounded bg-muted overflow-hidden border border-border"><div className="h-full transition-[width] duration-500" style={{ width: `${pct}%`, background: color }} /></div></div>);
}

const TABS = [["sparring", "Sparring", Users], ["rivals", "Rivals", Crosshair], ["gauntlet", "The Gauntlet", Skull]];

export default function Arena() {
  const { pet, patchUser } = useOutletContext();
  const [tab, setTab] = useState("sparring");
  const [ops, setOps] = useState(null);
  const [rivals, setRivals] = useState([]);
  const [grudges, setGrudges] = useState([]);
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [o, s, r, g] = await Promise.all([
      api.get("/arena/opponents"), api.get("/arena/state"),
      api.get("/arena/rivals"), api.get("/arena/grudges"),
    ]);
    setOps(o.data.opponents); setB(s.data.battle); setRivals(r.data.rivals); setGrudges(g.data.grudges);
  };
  useEffect(() => { load(); }, []);
  if (!ops) return <PageLoader label="Entering the Arena" />;

  const start = async (id) => { setBusy(true); try { const { data } = await api.post("/arena/start", { pet_id: id }); setB(data.battle); playSfx("power"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const startGauntlet = async () => { setBusy(true); try { const { data } = await api.post("/arena/gauntlet/start"); setB(data.battle); playSfx("power"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); } };
  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post("/arena/action", { message: action }); setB(data.battle);
      playSfx(action === "power" ? "power" : action === "defend" ? "pop" : "hit");
      if (data.battle.status === "won") { playJingle(pet?.cpu); if (data.battle.reward) patchUser({ credits: data.battle.reward.total_credits, bits: data.battle.reward.total_credits }); }
      else if (data.battle.status === "lost") playSfx("lose");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const flee = async () => { await api.post("/arena/flee"); setB(null); };
  const finish = async () => { await api.post("/arena/flee"); setB(null); load(); };

  if (b && b.status === "active") {
    const gauntlet = b.mode === "gauntlet";
    return (
      <div data-testid="arena-battle" className="max-w-2xl mx-auto">
        <button onClick={flee} className="btn-ghost mb-4"><ArrowLeft className="w-4 h-4" /> Flee</button>
        <div className="panel-glow p-5 hud-corner">
          {gauntlet && <div data-testid="gauntlet-stage" className="text-center label-cap text-accent mb-3 flex items-center justify-center gap-2"><Skull className="w-4 h-4" /> The Gauntlet — Round {(b.stage || 0) + 1} of {b.stages || 3} · No healing</div>}
          <div className="grid grid-cols-2 gap-5 mb-5">
            <div className="text-center">
              <div className="mx-auto w-fit"><LayeredStack layers={pet?.layers} core={pet?.core_color} rig={pet?.rig} cpu={pet?.cpu} size={140} /></div>
              <div className="font-head font-bold mt-1">{pet?.name}</div>
              <Bar label="You" hp={b.pet_hp} max={b.pet_max} color="hsl(var(--primary))" testId="pet-health" />
            </div>
            <div className="text-center">
              <div className="mx-auto w-fit" style={{ filter: `drop-shadow(0 0 12px ${b.opp_tint})` }}><Skull className="w-24 h-24" style={{ color: b.opp_tint }} /></div>
              <div className="font-head font-bold mt-1" style={{ color: b.opp_tint }}>{b.opp_name}</div>
              <Bar label="Enemy" hp={b.opp_hp} max={b.opp_max} color={b.opp_tint} testId="opp-health" />
            </div>
          </div>
          <div className="panel p-3 h-24 overflow-y-auto mb-4 text-sm space-y-1" data-testid="battle-log">
            {b.log.map((l, i) => <div key={i} className={l.includes("VICTORY") || l.includes("CLEARED") ? "text-secondary font-bold" : l.includes("OVERDRIVE") ? "text-accent font-bold" : "text-muted-foreground"}>{l}</div>)}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => act("attack")} data-testid="action-attack" disabled={busy} className="btn-primary flex-col py-3 gap-1"><Swords className="w-5 h-5" />Attack</button>
            <button onClick={() => act("power")} data-testid="action-power" disabled={busy || b.overdrive <= 0} className="btn-secondary flex-col py-3 gap-1"><Zap className="w-5 h-5" />Overdrive ({b.overdrive})</button>
            <button onClick={() => act("defend")} data-testid="action-defend" disabled={busy || gauntlet} title={gauntlet ? "Healing is disabled in The Gauntlet" : ""} className="btn-ghost flex-col py-3 gap-1"><Shield className="w-5 h-5" />Defend</button>
          </div>
        </div>
      </div>
    );
  }

  if (b && (b.status === "won" || b.status === "lost")) {
    const won = b.status === "won";
    return (
      <div data-testid="arena-result" className="max-w-md mx-auto panel-glow p-8 text-center hud-corner">
        {won ? <Trophy className="w-14 h-14 mx-auto text-secondary mb-3" /> : <Skull className="w-14 h-14 mx-auto text-accent mb-3" />}
        <h1 className="font-head font-black text-2xl">{won ? (b.mode === "gauntlet" ? "Gauntlet Cleared!" : "Victory!") : "Defeated"}</h1>
        {won && b.reward && <p className="text-secondary font-bold mt-2">+{b.reward.credits} Bits</p>}
        {!won && <p className="text-muted-foreground mt-2 text-sm">This foe has been added to your <span className="text-accent font-bold">Rivals</span> list — avenge the loss!</p>}
        <button onClick={finish} data-testid="arena-continue" className="btn-primary mt-6">Back to Arena</button>
      </div>
    );
  }

  const PvpCard = ({ r, kind }) => (
    <div key={r.pet_id} data-testid={`${kind}-${r.pet_id}`} className="panel p-4 flex flex-col items-center text-center">
      <LayeredStack layers={r.layers} core={r.core_color} rig={r.rig} cpu={r.cpu} size={92} />
      <h3 className="font-head font-bold text-sm mt-2">{r.name}</h3>
      <p className="label-cap text-[9px] flex items-center gap-1"><User className="w-3 h-3" />{r.owner}</p>
      <div className="flex items-center gap-1 text-xs mono my-2"><Flame className="w-3.5 h-3.5 text-accent" /> PWR {r.power}</div>
      <button onClick={() => start(r.pet_id)} data-testid={`fight-${r.pet_id}`} disabled={busy}
        className={kind === "rival" ? "btn-secondary w-full text-xs" : "btn-primary w-full text-xs"}>
        {kind === "rival" ? <><Crosshair className="w-4 h-4" />Avenge</> : <><Swords className="w-4 h-4" />Duel</>}
      </button>
    </div>
  );

  return (
    <div data-testid="arena-page">
      <span className="label-cap text-primary flex items-center gap-2"><Swords className="w-4 h-4" /> Battle Arena // Arena Master</span>
      <h1 className="text-4xl font-black tracking-tighter mt-2 mb-2 font-head">Battle Arena</h1>
      <p className="text-muted-foreground mb-5">Your robot's stats + equipped battle-grid gear decide the fight.</p>

      <div className="flex gap-1 mb-5 p-1 rounded-[var(--radius)] bg-muted w-fit">
        {TABS.map(([id, l, Icon]) => (
          <button key={id} data-testid={`arena-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono flex items-center gap-1.5 ${tab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <Icon className="w-3.5 h-3.5" />{l}</button>
        ))}
      </div>

      {tab === "sparring" && (
        <div className="grid md:grid-cols-3 gap-4" data-testid="sparring-list">
          <p className="col-span-full text-sm text-muted-foreground -mt-2 mb-1">Standard individual matches against NPC training bots — selectable any time.</p>
          {ops.map((o) => (
            <div key={o.id} data-testid={`opponent-${o.id}`} className="panel overflow-hidden flex flex-col">
              <div className="h-28 grid place-items-center" style={{ background: `radial-gradient(circle,${o.tint}55,transparent 75%)` }}><Skull className="w-14 h-14" style={{ color: o.tint }} /></div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-head font-bold" style={{ color: o.tint }}>{o.name}</h3>
                <p className="text-sm text-muted-foreground flex-1 mt-1">{o.desc}</p>
                <div className="flex gap-3 text-xs my-2"><span className="flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-accent" />{o.hp}</span><span className="flex items-center gap-1"><Swords className="w-3.5 h-3.5 text-secondary" />{o.attack}</span><span className="text-secondary font-bold ml-auto">+{o.reward} Bits</span></div>
                <button onClick={() => start(o.id)} data-testid={`fight-${o.id}`} disabled={busy || !o.unlocked} className={o.unlocked ? "btn-primary w-full" : "btn-ghost w-full opacity-60"}>{o.unlocked ? <><Swords className="w-4 h-4" />Spar</> : `SCL-${o.min_scl}`}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "rivals" && (
        <div data-testid="rivals-list">
          <p className="text-sm text-muted-foreground -mt-2 mb-3">Challenge other pilots' robots, or hit back at rivals who defeated you.</p>
          {grudges.length > 0 && (<><div className="label-cap text-accent mb-2 flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5" /> Revenge Logs</div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">{grudges.map((r) => <PvpCard key={r.pet_id} r={r} kind="rival" />)}</div></>)}
          <div className="label-cap mb-2 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Active Pilots</div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {rivals.length === 0 && <div className="col-span-full panel p-8 text-center text-muted-foreground">No other pilots online yet. Check back soon!</div>}
            {rivals.map((r) => <PvpCard key={r.pet_id} r={r} kind="spar" />)}
          </div>
        </div>
      )}

      {tab === "gauntlet" && (
        <div data-testid="gauntlet-lobby" className="panel-glow p-8 text-center hud-corner max-w-lg mx-auto">
          <Skull className="w-16 h-16 mx-auto text-accent mb-3" />
          <h2 className="font-head font-black text-2xl mb-2">The Gauntlet</h2>
          <p className="text-muted-foreground text-sm mb-4">Face <b className="text-foreground">3 escalating NPC bots back-to-back</b>. Your HP carries between rounds and <b className="text-accent">healing / Defend is disabled</b>. Enemies scale in level, stats and aggression. Clear all three for a big Bit payout.</p>
          <div className="flex items-center justify-center gap-2 mb-6 text-xs">
            <span className="px-2 py-1 rounded-full border border-emerald-400/50 text-emerald-400">Round 1</span>
            <span className="text-muted-foreground">›</span>
            <span className="px-2 py-1 rounded-full border border-sky-400/50 text-sky-400">Round 2</span>
            <span className="text-muted-foreground">›</span>
            <span className="px-2 py-1 rounded-full border border-red-400/50 text-red-400">Round 3</span>
          </div>
          <button onClick={startGauntlet} data-testid="gauntlet-start-btn" disabled={busy} className="btn-primary text-base px-10 py-4"><Layers className="w-5 h-5" /> Enter the Gauntlet</button>
        </div>
      )}
    </div>
  );
}
